import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { formatISO, parseISO } from 'date-fns';

import { Stack } from '@reckon-web/stack';
import { Button, IconButton } from '@reckon-web/button';
import { Edit3Icon } from '@reckon-web/icon';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { LoadingDots } from '@reckon-web/loading';
import { Box } from '@reckon-web/box';
import {
  FilterMenu,
  // SelectMenu,
  SelectMenuButton,
} from '@reckon-web/select-menu';
import { Tag } from '@reckon-web/tag';
import { Inline } from '@reckon-web/inline';
import { DataGroup, DataRow } from '@reckon-web/data-group';
import { useTheme } from '@reckon-web/theme';
import { makeId } from '@reckon-web/utils';
import { Tooltip } from '@reckon-web/tooltip';

import { CurrencyField } from '../../common/currency-field';
import { DatePickerField, ISODate } from '../../common/date-picker-field';
import type { Job } from '../../../hooks/use-query-job';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { DueDate } from '../../common/due-date';
import { DataRowInlineEditOffset } from '../../common/data-row-inline-edit-offset';
import { numberToCurrencyString } from '../../../utils/currency-utils';
import { useGetEntitiesByIds } from '../../../data/useGetEntitiesByIds';
import { getRecurrenceFrequencyLabel } from '../../../globals';
import {
  GetMemberListItem,
  useGetMembersList,
} from '../../../data/useGetMembersList';
import { useTenantTimezoneConversion } from '../../../hooks/use-tenant-timezone';
import { AvatarHelper } from '../../notifications/notification/notification-helper/avatar-helper';
import { buildAssignableUsers } from '../../task/drawer';
import { RescheduleDrawer } from '../../notifications/recurrence/reschedule-drawer';
import { DateUtils } from '../../../utils/date-utils';
import { EditClientsDrawer } from '../edit-clients-drawer';
import { useCheckDupliateJobName } from '../../../hooks/use-check-duplicate-job-name';
import { Columns } from '../../../../../../design-system/packages/columns/src';
import { TextField } from '../../common/text-field';
import { UpdateJobInput } from '../../../data/useUpdateJob';
import {
  JobStatusEnum,
  JobStatusEnumValueType,
} from '../types/job-status-enum';

import { JobUpdateErrors } from './view';

const jobStatusesToHideDueDaysFor = new Set<JobStatusEnumValueType>([
  JobStatusEnum.valuesEnum.Completed,
  JobStatusEnum.valuesEnum.Closed,
]);

export const Details: React.FC<{
  job: Job;
  partialUpdate?: (vals: Partial<UpdateJobInput>) => Promise<boolean>;
  externalErrors?: JobUpdateErrors;
  setShouldPromptUnsaved: Dispatch<SetStateAction<boolean | undefined>>;
  testId?: string;
}> = ({
  job,
  partialUpdate,
  externalErrors,
  setShouldPromptUnsaved,
  testId = `details`,
}) => {
  // We also need the entities, they're not loaded by anything
  const getEntitiesByIdsArgs: Parameters<typeof useGetEntitiesByIds>[0] =
    useMemo(() => {
      return { variables: { cloudIds: job?.entities || [] }, skip: !job };
    }, [job]);
  const {
    loading: loadingEntities,
    // error: errorEntities,
    data: jobEntities,
  } = useGetEntitiesByIds(getEntitiesByIdsArgs);

  const sortedEntities = useMemo(() => {
    if (loadingEntities || !jobEntities) {
      return [];
    }
    return jobEntities
      .map((x) => ({ name: x.name || '', cloudId: x.cloudId }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
  }, [loadingEntities, jobEntities]);

  const {
    loading: usersLoading,
    data: usersData,
    error: usersError,
  } = useGetMembersList();

  const theme = useTheme();
  const { localDateToTenantToUtc } = useTenantTimezoneConversion();

  // holding pen for values on screen
  const [name, setName] = useState('');
  // memo around this because we need it to be stable
  const nameError = useMemo(
    () => (!name ? 'Job name is required' : undefined),
    [name]
  );
  const [CheckDupliateNameDialog, checkDuplicateName] =
    useCheckDupliateJobName();

  const [budget, setBudget] = useState<string | number>('');
  // memo around this because we need it to be stable
  const budgetError = useMemo(
    () =>
      typeof budget === 'string' && budget !== ''
        ? 'Budget must be a number'
        : undefined,
    [budget]
  );

  const [dueDate, setDueDate] = useState<ISODate>();
  const dueDateParsed = dueDate ? parseISO(dueDate) : undefined;

  const [startDate, setStartDate] = useState<ISODate>();
  const startDateParsed = startDate ? parseISO(startDate) : undefined;

  const [partner, setPartner] = useState<GetMemberListItem>();
  const [manager, setManager] = useState<GetMemberListItem>();
  const [staff, setStaff] = useState<GetMemberListItem>();
  const [assignee, setAssignee] = useState<GetMemberListItem>();

  const [partnerSearchTerm, setPartnerSearchTerm] = useState<string>();
  const [managerSearchTerm, setManagerSearchTerm] = useState<string>();
  const [staffSearchTerm, setStaffSearchTerm] = useState<string>();
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState<string>();

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [resetRescheduleJob, setResetRescheduleJob] = useState(Symbol());

  const openRescheduleJob = () => {
    setResetRescheduleJob(Symbol());
    setIsRescheduling(true);
  };

  const [isEditingClients, setIsEditingClients] = useState(false);
  const [resetEditClients, setResetEditClients] = useState(Symbol());

  const openEditClients = () => {
    setResetEditClients(Symbol());
    setIsEditingClients(true);
  };

  const partnerOptions: GetMemberListItem[] = useMemo(() => {
    return buildAssignableUsers({
      users: (!usersLoading && !usersError && usersData) || undefined,
      originalUserId: job.partnerUserId,
      searchTerm: partnerSearchTerm,
      partner: true,
    });
  }, [usersLoading, usersData, usersError, job, partnerSearchTerm]);
  const managerOptions: GetMemberListItem[] = useMemo(() => {
    return buildAssignableUsers({
      users: (!usersLoading && !usersError && usersData) || undefined,
      originalUserId: job.managerUserId,
      searchTerm: managerSearchTerm,
      manager: true,
    });
  }, [usersLoading, usersData, usersError, job, managerSearchTerm]);
  const staffOptions: GetMemberListItem[] = useMemo(() => {
    return buildAssignableUsers({
      users: (!usersLoading && !usersError && usersData) || undefined,
      originalUserId: job.staffUserId,
      searchTerm: staffSearchTerm,
    });
  }, [usersLoading, usersData, usersError, job, staffSearchTerm]);
  const assigneeOptions: GetMemberListItem[] = useMemo(() => {
    return buildAssignableUsers({
      users: (!usersLoading && !usersError && usersData) || undefined,
      originalUserId: job.assigneeUserId,
      searchTerm: assigneeSearchTerm,
    });
  }, [usersLoading, usersData, usersError, job, assigneeSearchTerm]);

  useEffect(() => {
    setName(job.name || '');
    setDueDate(
      job.dueDate
        ? (formatISO(job.dueDate, { representation: 'date' }) as ISODate)
        : undefined
    );
    setStartDate(
      job.startDate
        ? (formatISO(job.startDate, { representation: 'date' }) as ISODate)
        : undefined
    );
    setBudget(typeof job.budget === 'number' ? job.budget : '');
    setPartner(usersData?.find((u) => u.cloudId === job.partnerUserId));
    setManager(usersData?.find((u) => u.cloudId === job.managerUserId));
    setStaff(usersData?.find((u) => u.cloudId === job.staffUserId));
    setAssignee(usersData?.find((u) => u.cloudId === job.assigneeUserId));
  }, [job, usersData]);

  useEffect(
    () => setShouldPromptUnsaved(Boolean(nameError || budgetError)),
    [setShouldPromptUnsaved, nameError, budgetError]
  );

  return (
    <Box paddingY="xlarge" data={{ testid: testId }}>
      <DataGroup template={[1, 3]}>
        {partialUpdate && (
          <DataRow label="Job name*">
            <DataRowInlineEditOffset childType="small-input">
              <TextField
                label="Job name*"
                size="small"
                placeholder="Enter job name"
                weight="none"
                labelVisible={false}
                value={name}
                onBlur={async () => {
                  //only update if we haven't pinged them for an error
                  if (nameError) {
                    return;
                  }
                  // a job must have a client, but graphql and typescript don't know that, so this is a "shut up compiler" check
                  if (!job.clientId || !job.client?.name) {
                    return;
                  }
                  const canSaveBasedOnDupeNameCheck = await checkDuplicateName({
                    jobId: job.cloudId || '',
                    jobName: name,
                    debtorId: job.clientId,
                    debtorName: job.client?.name,
                  });
                  if (!canSaveBasedOnDupeNameCheck) {
                    setName(job.name || '');
                    // TODO this REALLY should refocus. But it does not
                    return;
                  }

                  partialUpdate({ name });
                }}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                invalidMessage={nameError || externalErrors?.name?.join('. ')}
                /* TODO Workflow-400 once balance supports test id's, replace this ID */
                id={makeId(testId, 'job-name')}
              />
            </DataRowInlineEditOffset>
          </DataRow>
        )}
        <DataRow label="Assignee">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <FilterMenu<GetMemberListItem>
                onChange={(value) => {
                  setAssignee(value);

                  partialUpdate({ assigneeUserId: value.cloudId });
                }}
                onInputChange={(inputValue) =>
                  setAssigneeSearchTerm(inputValue)
                }
                options={assigneeOptions}
                trigger={({ triggerProps, hasValue }) => (
                  <SelectMenuButton
                    weight="subtle"
                    onClear={() => {
                      setAssignee(undefined);

                      partialUpdate({ assigneeUserId: undefined });
                    }}
                    hasValue={hasValue}
                    {...triggerProps}
                    data-testid={makeId(testId, 'assignee-select-menu-button')}
                  >
                    {assignee ? (
                      <NamedUserAvatar
                        user={assignee}
                        avatarSize="xsmall"
                        textSize="small"
                        textColor="base"
                        data-testid={makeId(testId, 'assignee-avatar')}
                      />
                    ) : (
                      // this really needs to be the placeholder colour so it looks correct next to the other proper inputs
                      <Text
                        css={{ color: theme.palette.formInput.textPlaceholder }}
                        size="small"
                        weight="medium"
                        leading="tight"
                      >
                        Select assignee
                      </Text>
                    )}
                  </SelectMenuButton>
                )}
                value={assignee}
                itemRenderer={(option) => (
                  <NamedUserAvatar
                    user={option}
                    avatarSize="xsmall"
                    textSize="small"
                    data-testid={makeId(testId, 'assignee-option-avatar')}
                  />
                )}
                itemToLabel={(option) => option.fullName || ''}
                itemToValue={(option) => option.cloudId || ''}
                data-testid={makeId(testId, 'assignee')}
              />
            </DataRowInlineEditOffset>
          ) : job.assigneeUserId ? (
            <DataRowInlineEditOffset childType="tags">
              <AvatarHelper
                initiatedBy_user_cloudId={job.assigneeUserId}
                initiatedBy_user_name="loading"
                data-testid={makeId(testId, 'assignee-avatar-helper')}
              />
            </DataRowInlineEditOffset>
          ) : undefined}
        </DataRow>
        <DataRow label="Debtor">
          <Box
            paddingLeft={partialUpdate ? 'medium' : 'none'}
            data={{ testid: makeId(testId, 'debtor') }}
          >
            {job.client?.name || <span>&mdash;</span>}
          </Box>
        </DataRow>
        <DataRow label="Clients">
          <Box
            paddingLeft={partialUpdate ? 'medium' : 'none'}
            data={{ testid: makeId(testId, 'clients') }}
          >
            {loadingEntities ? (
              <Flex alignItems="center" height={theme.sizing.xsmall}>
                <LoadingDots
                  label="Loading clients"
                  size="small"
                  data-testid={makeId(testId, 'loading-clients')}
                />
              </Flex>
            ) : (
              <Columns gap="xsmall" template={[1, 0]}>
                <DataRowInlineEditOffset childType="tags">
                  <Inline
                    gap="xsmall"
                    data={{ testid: makeId(testId, 'entity-tags') }}
                  >
                    {sortedEntities.map((entity) => (
                      <Tooltip
                        key={entity.cloudId}
                        content={
                          <Text overflowStrategy="breakword">
                            {entity.name}
                          </Text>
                        }
                      >
                        <Tag
                          label={entity.name}
                          data={{
                            testid: makeId(
                              testId,
                              'entity-tag',
                              entity.cloudId
                            ),
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Inline>
                </DataRowInlineEditOffset>

                {partialUpdate && (
                  <DataRowInlineEditOffset childType="small-input">
                    <IconButton
                      size="small"
                      icon={Edit3Icon}
                      label="Edit clients"
                      weight="none"
                      onClick={openEditClients}
                      data-testid={makeId(testId, 'edit-clients')}
                    />
                  </DataRowInlineEditOffset>
                )}
              </Columns>
            )}
          </Box>
        </DataRow>
        <DataRow label="Partner">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <FilterMenu<GetMemberListItem>
                onChange={(value) => {
                  setPartner(value);

                  partialUpdate({ partnerUserId: value.cloudId });
                }}
                onInputChange={(inputValue) => setPartnerSearchTerm(inputValue)}
                options={partnerOptions}
                trigger={({ triggerProps, hasValue }) => (
                  <SelectMenuButton
                    weight="subtle"
                    onClear={() => {
                      setPartner(undefined);

                      partialUpdate({ partnerUserId: undefined });
                    }}
                    hasValue={hasValue}
                    {...triggerProps}
                    data-testid={makeId(testId, 'partner-select-menu-button')}
                  >
                    {partner ? (
                      <NamedUserAvatar
                        user={partner}
                        avatarSize="xsmall"
                        textSize="small"
                        textColor="base"
                        data-testid={makeId(testId, 'partner-avatar')}
                      />
                    ) : (
                      // this really needs to be the placeholder colour so it looks correct next to the other proper inputs
                      <Text
                        css={{ color: theme.palette.formInput.textPlaceholder }}
                        size="small"
                        weight="medium"
                        leading="tight"
                      >
                        Select partner
                      </Text>
                    )}
                  </SelectMenuButton>
                )}
                value={partner}
                itemRenderer={(option) => (
                  <NamedUserAvatar
                    user={option}
                    avatarSize="xsmall"
                    textSize="small"
                    data-testid={makeId(testId, 'partner-option-avatar')}
                  />
                )}
                itemToLabel={(option) => option.fullName || ''}
                itemToValue={(option) => option.cloudId || ''}
                data-testid={makeId(testId, 'partner')}
              />
            </DataRowInlineEditOffset>
          ) : job.partnerUserId ? (
            <DataRowInlineEditOffset childType="tags">
              <AvatarHelper
                initiatedBy_user_cloudId={job.partnerUserId}
                initiatedBy_user_name="loading"
                data-testid={makeId(testId, 'partner-avatar-helper')}
              />
            </DataRowInlineEditOffset>
          ) : undefined}
        </DataRow>
        <DataRow label="Manager">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <FilterMenu<GetMemberListItem>
                onChange={(value) => {
                  setManager(value);

                  partialUpdate({ managerUserId: value.cloudId });
                }}
                onInputChange={(inputValue) => setManagerSearchTerm(inputValue)}
                options={managerOptions}
                trigger={({ triggerProps, hasValue }) => (
                  <SelectMenuButton
                    weight="subtle"
                    onClear={() => {
                      setManager(undefined);

                      partialUpdate({ managerUserId: undefined });
                    }}
                    hasValue={hasValue}
                    {...triggerProps}
                    data-testid={makeId(testId, 'manager-select-menu-button')}
                  >
                    {manager ? (
                      <NamedUserAvatar
                        user={manager}
                        avatarSize="xsmall"
                        textSize="small"
                        textColor="base"
                        data-testid={makeId(testId, 'manager-avatar')}
                      />
                    ) : (
                      // this really needs to be the placeholder colour so it looks correct next to the other proper inputs
                      <Text
                        css={{ color: theme.palette.formInput.textPlaceholder }}
                        size="small"
                        weight="medium"
                        leading="tight"
                      >
                        Select manager
                      </Text>
                    )}
                  </SelectMenuButton>
                )}
                value={manager}
                itemRenderer={(option) => (
                  <NamedUserAvatar
                    user={option}
                    avatarSize="xsmall"
                    textSize="small"
                    data-testid={makeId(testId, 'manager-option-avatar')}
                  />
                )}
                itemToLabel={(option) => option.fullName || ''}
                itemToValue={(option) => option.cloudId || ''}
                data-testid={makeId(testId, 'manager')}
              />
            </DataRowInlineEditOffset>
          ) : job.managerUserId ? (
            <DataRowInlineEditOffset childType="tags">
              <AvatarHelper
                initiatedBy_user_cloudId={job.managerUserId}
                initiatedBy_user_name="loading"
                data-testid={makeId(testId, 'manager-avatar-helper')}
              />
            </DataRowInlineEditOffset>
          ) : undefined}
        </DataRow>
        <DataRow label="Staff">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <FilterMenu<GetMemberListItem>
                onChange={(value) => {
                  setStaff(value);

                  partialUpdate({ staffUserId: value.cloudId });
                }}
                onInputChange={(inputValue) => setStaffSearchTerm(inputValue)}
                options={staffOptions}
                trigger={({ triggerProps, hasValue }) => (
                  <SelectMenuButton
                    weight="subtle"
                    onClear={() => {
                      setStaff(undefined);

                      partialUpdate({ staffUserId: undefined });
                    }}
                    hasValue={hasValue}
                    {...triggerProps}
                    data-testid={makeId(testId, 'staff-select-menu-button')}
                  >
                    {staff ? (
                      <NamedUserAvatar
                        user={staff}
                        avatarSize="xsmall"
                        textSize="small"
                        textColor="base"
                        data-testid={makeId(testId, 'staff-avatar')}
                      />
                    ) : (
                      // this really needs to be the placeholder colour so it looks correct next to the other proper inputs
                      <Text
                        css={{ color: theme.palette.formInput.textPlaceholder }}
                        size="small"
                        weight="medium"
                        leading="tight"
                      >
                        Select staff
                      </Text>
                    )}
                  </SelectMenuButton>
                )}
                value={staff}
                itemRenderer={(option) => (
                  <NamedUserAvatar
                    user={option}
                    avatarSize="xsmall"
                    textSize="small"
                    data-testid={makeId(testId, 'staff-option-avatar')}
                  />
                )}
                itemToLabel={(option) => option.fullName || ''}
                itemToValue={(option) => option.cloudId || ''}
                data-testid={makeId(testId, 'staff')}
              />
            </DataRowInlineEditOffset>
          ) : job.staffUserId ? (
            <DataRowInlineEditOffset childType="tags">
              <AvatarHelper
                initiatedBy_user_cloudId={job.staffUserId}
                initiatedBy_user_name="loading"
                data-testid={makeId(testId, 'staff-avatar-helper')}
              />
            </DataRowInlineEditOffset>
          ) : undefined}
        </DataRow>
        <DataRow label="Budget">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <CurrencyField
                label="Budget"
                labelVisible={false}
                weight="none"
                size="small"
                placeholder="Enter budget"
                value={budget}
                invalidMessage={
                  budgetError || externalErrors?.budget?.join('. ')
                }
                onChange={(value) => {
                  setBudget(value);
                }}
                onBlur={() => {
                  if (budgetError) {
                    return;
                  }
                  const budgetParsed =
                    typeof budget === 'number' ? budget : null;
                  partialUpdate({ budget: budgetParsed });
                }}
                /* TODO Workflow-400 once balance supports test id's, replace this ID */
                id={makeId(testId, 'budget')}
              />
            </DataRowInlineEditOffset>
          ) : typeof budget === 'number' ? (
            <Text
              size="small"
              weight="medium"
              data={{ testid: makeId(testId, 'budget') }}
            >
              {numberToCurrencyString(budget)}
            </Text>
          ) : undefined}
        </DataRow>
        <DataRow label="Start date">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <Stack gap="none">
                <DatePickerField
                  label="Start date"
                  size="small"
                  labelVisible={false}
                  invalidMessage={externalErrors?.startDate?.join('. ')}
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    partialUpdate({
                      startDate: localDateToTenantToUtc(value) || null,
                    });
                  }}
                  onClear={() => {
                    setStartDate(undefined);
                    partialUpdate({ startDate: null });
                  }}
                  weight="none"
                  /* TODO Workflow-400 once balance supports test id's, replace this ID */
                  id={makeId(testId, 'start-date')}
                />
              </Stack>
            </DataRowInlineEditOffset>
          ) : startDateParsed ? (
            <DueDate
              date={startDateParsed}
              showDate={true}
              showDueDays={false}
              testId={makeId(testId, 'start-date-show-date-true')}
            />
          ) : undefined}
        </DataRow>
        <DataRow label="Due date">
          {partialUpdate ? (
            <DataRowInlineEditOffset childType="small-input">
              <Stack gap="none">
                <DatePickerField
                  label="Due date"
                  size="small"
                  labelVisible={false}
                  invalidMessage={externalErrors?.dueDate?.join('. ')}
                  value={dueDate}
                  onChange={(value) => {
                    setDueDate(value);
                    partialUpdate({
                      dueDate: localDateToTenantToUtc(value) || null,
                    });
                  }}
                  onClear={() => {
                    setDueDate(undefined);
                    partialUpdate({ dueDate: null });
                  }}
                  weight="none"
                  /* TODO Workflow-400 once balance supports test id's, replace this ID */
                  id={makeId(testId, 'due-date')}
                />
                {dueDateParsed &&
                  Boolean(
                    job.status.status &&
                      !jobStatusesToHideDueDaysFor.has(job.status.status)
                  ) && (
                    <Box paddingLeft="medium" paddingBottom="small">
                      <DueDate
                        date={dueDateParsed}
                        showDate={false}
                        size="xsmall"
                        showDueDays={true}
                        testId={makeId(testId, 'due-date-show-date-false')}
                      />
                    </Box>
                  )}
              </Stack>
            </DataRowInlineEditOffset>
          ) : dueDateParsed ? (
            <DueDate
              date={dueDateParsed}
              showDate={true}
              showDueDays={Boolean(
                job.status.status &&
                  !jobStatusesToHideDueDaysFor.has(job.status.status)
              )}
              testId={makeId(testId, 'due-date-show-date-true')}
            />
          ) : undefined}
        </DataRow>
        <DataRow label="Recurrence">
          {job.recurrence?.enabled &&
          job.recurrence.nextDateUtc &&
          job.recurrence.recurrenceFrequency ? (
            <Flex wrap={false} paddingLeft="medium">
              <Stack gap="small" flex={1}>
                <Inline alignY="bottom" gap="xsmall">
                  <Text
                    inline
                    size="small"
                    data={{ testid: makeId(testId, 'date') }}
                  >
                    Next:{' '}
                    {DateUtils.formatDateShort(job.recurrence.nextDateUtc)}
                  </Text>
                  <Text
                    size="xsmall"
                    weight="medium"
                    inline
                    color={
                      new Date() > job.recurrence.nextDateUtc
                        ? 'critical'
                        : 'muted'
                    }
                    data={{ testid: makeId(testId, 'date-ago') }}
                  >
                    (
                    {DateUtils.formatDueDays(job.recurrence.nextDateUtc, 'ago')}
                    )
                  </Text>
                </Inline>
                <Text
                  size="xsmall"
                  inline
                  color="muted"
                  data={{ testid: makeId(testId, 'date-recurrence') }}
                >
                  Then every{' '}
                  {getRecurrenceFrequencyLabel(
                    job.recurrence?.recurrenceFrequency
                  )}
                </Text>
              </Stack>
              <DataRowInlineEditOffset childType="small-input">
                <IconButton
                  size="small"
                  icon={Edit3Icon}
                  label="Reschedule"
                  weight="none"
                  onClick={openRescheduleJob}
                  data-testid={makeId(testId, 'reschedule')}
                />
              </DataRowInlineEditOffset>
            </Flex>
          ) : (
            // Show "Recur this job" button if no recurrence is set
            <DataRowInlineEditOffset childType="small-input">
              <Button
                weight="none"
                tone="active"
                label="Recur this job"
                size="small"
                onClick={openRescheduleJob}
                data-testid={makeId(testId, 'recur-this-job')}
              />
            </DataRowInlineEditOffset>
          )}
        </DataRow>
      </DataGroup>
      <CheckDupliateNameDialog />
      <RescheduleDrawer
        reset={resetRescheduleJob}
        drawerOpen={isRescheduling}
        onClose={() => setIsRescheduling(false)}
        recurringJob={job}
        testId={makeId(testId, 'reschedule-job')}
      />
      {partialUpdate && (
        <EditClientsDrawer
          reset={resetEditClients}
          drawerOpen={isEditingClients}
          doUpdateClients={(cloudIds) => partialUpdate({ entities: cloudIds })}
          job={job}
          onClose={() => setIsEditingClients(false)}
          externalErrors={externalErrors}
          testId={makeId(testId, 'edit-clients')}
        />
      )}
    </Box>
  );
};
